import { useState } from "react";
import { ERROR_MESSAGES } from "../../constant/constant";
import axios from "axios";
import { TENANT_DOMAIN } from "../../utils/domain";

/**
 * @typedef {Object} ResetPasswordParams
 * @property {string} token - Password reset token
 * @property {string} password - New password
 * @property {string} confirmPassword - Confirm password (must match password)
 * @property {string} email - User's email address
 * @property {string} role - User's email address
 */

/**
 * Custom hook for handling tenant password reset functionality.
 *
 * @description
 * This hook manages the password reset process for tenant users, including:
 * - Loading state management
 * - Error handling
 * - API communication with the tenant auth endpoint
 */

export const useTenantResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /**
   * Executes the reset password API request.
   * @async
   * @param {ResetPasswordParams} params - Object containing required reset password information
   * @param {Function} onSuccess - Callback function to execute on successful reset
   * @returns {Promise<void>} Resolves once the API request completes
   */
  const resetPassword = async (params, onSuccess) => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(
        `${TENANT_DOMAIN}/tenant/auth/reset-password`,
        {
          password: params.password,
          password_confirmation: params.confirmPassword,
          token: params.token,
          email: params.email,
          role: params.role,
        },
      );
      console.log(response);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      if (err.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    setError,
    resetPassword,
  };
};
